import { Link } from 'react-router-dom';
import { products } from '../mock';
import { ArrowUpRight, Check, Circle } from 'lucide-react';
import { TopoLines } from './graphics/Decor';
const accentMap = {
  navy: 'from-[#0B2545] to-[#13315C]',
  steel: 'from-[#3E5C76] to-[#5B7B99]',
  slate: 'from-[#748CAB] to-[#8DA9C4]',
};

const MOTIF_SRC = {
  axiom: { src: '/brand-art/axiom-underwriter-mark.png', cls: 'top-6 right-6 w-24' },
  art: { src: '/brand-art/art-tree.png', cls: '-top-2 -right-4 w-44' },
  truenorth: { src: '/brand-art/truenorth-mark.png', cls: '-top-2 -right-4 w-44' },
};

const CardMotif = ({ productId }) => (
  <img
    src={MOTIF_SRC[productId].src}
    alt=""
    aria-hidden
    className={`absolute ${MOTIF_SRC[productId].cls} opacity-[0.14] mix-blend-multiply pointer-events-none group-hover:opacity-[0.28] transition-opacity`}
  />
);

const ProductCard = ({ product }) => (
  <Link
    to={`/product/${product.id}`}
    className="group relative flex flex-col rounded-2xl border border-slate-200 bg-white p-7 hover:border-slate-300 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 overflow-hidden"
    data-testid={`product-card-${product.id}`}
  >
    <CardMotif productId={product.id} />
    <div className={`h-1 w-12 rounded-full bg-gradient-to-r ${accentMap[product.accent]}`} />
    <div className="mt-5 flex items-center justify-between">
      <div className="font-serif text-3xl font-semibold text-[#0B2545]">{product.name}</div>
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200/60 px-2.5 py-1 text-[11px] font-medium">
        <Circle className="w-1.5 h-1.5 fill-emerald-500 text-emerald-500" />
        {product.status}
      </span>
    </div>
    <div className="mt-1 text-[13px] font-medium text-slate-500 tracking-wide uppercase">
      {product.tagline}
    </div>
    <p className="mt-5 text-[14.5px] leading-relaxed text-slate-600">{product.description}</p>
    <ul className="mt-5 space-y-2">
      {product.bullets.map((b) => (
        <li key={b} className="flex items-start gap-2 text-[13.5px] text-slate-700">
          <Check className="w-4 h-4 mt-0.5 text-[#3E5C76]" />
          <span>{b}</span>
        </li>
      ))}
    </ul>
    <div className="mt-7 pt-5 border-t border-slate-100 flex items-center justify-between">
      <span className="text-[13.5px] font-medium text-[#0B2545] group-hover:text-[#3E5C76] transition-colors">
        Explore {product.name}
      </span>
      <div className="w-8 h-8 rounded-full bg-slate-50 group-hover:bg-[#0B2545] flex items-center justify-center transition-colors">
        <ArrowUpRight className="w-4 h-4 text-slate-500 group-hover:text-white transition-colors" />
      </div>
    </div>
  </Link>
);

const Products = () => (
  <section id="products" className="relative py-24 md:py-32 bg-white overflow-hidden">
    <TopoLines className="absolute top-0 right-0 w-2/3 h-56 pointer-events-none" opacity={0.35} />
    <div className="absolute -top-32 -left-16 w-[360px] h-[360px] rounded-full bg-gradient-to-br from-[#DDE5EE]/60 to-transparent blur-3xl pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
      <div className="max-w-2xl">
        <div className="text-[12px] uppercase tracking-[0.22em] font-semibold text-[#3E5C76]">
          Our Platform
        </div>
        <h2 className="mt-3 font-serif text-4xl md:text-5xl font-medium tracking-[-0.02em] text-[#0B2545]">
          Built for the industry,
          <br />
          <span className="text-slate-500">by the industry.</span>
        </h2>
        <p className="mt-5 text-[16px] leading-relaxed text-slate-600">
          Three powerful tools reimagining how title professionals work — in Washington and beyond.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl">
        {products.map((p) => (
          <ProductCard key={p.id} product={p} />
        ))}
      </div>
    </div>
  </section>
);

export default Products;
